// Names and colours for albums. Craving, region and meal albums are named here in every app
// language (the API only sends keys); the user's own albums carry the name they typed.
import type { AlbumCover, Script } from './types';
import { uiLang } from './i18n';

export interface AlbumName { en: string; ta: string; tg: string; hi: string; te: string; kn: string; ml: string }

export const CATALOG: Record<string, Record<string, AlbumName>> = {
  craving: {
    spicy: { en: 'Something spicy', ta: 'காரசாரமா', tg: 'kaarasaarama', hi: 'कुछ तीखा', te: 'కారంగా ఏదైనా', kn: 'ಏನಾದರೂ ಖಾರ', ml: 'എരിവുള്ളത്' },
    comfort: { en: 'Comfort food', ta: 'மனசுக்கு இதமா', tg: 'manasukku idhama', hi: 'सुकून वाला खाना', te: 'హాయినిచ్చే భోజనం', kn: 'ಮನಸ್ಸಿಗೆ ಹಿತ', ml: 'മനസ്സിന് സുഖം' },
    quick: { en: 'Quick fix', ta: 'சட்டுனு', tg: 'sattunu', hi: 'झटपट', te: 'త్వరగా అయ్యేవి', kn: 'ಬೇಗ ಆಗುವುದು', ml: 'പെട്ടെന്ന്' },
    teatime: { en: 'Tea-time snack', ta: 'டீ டைம்', tg: 'tea time', hi: 'चाय के साथ', te: 'టీ టైమ్ స్నాక్', kn: 'ಟೀ ಜೊತೆ ತಿಂಡಿ', ml: 'ചായക്കൊപ്പം' },
    light: { en: 'Light & healthy', ta: 'லேசா', tg: 'lesa', hi: 'हल्का और सेहतमंद', te: 'తేలికగా, ఆరోగ్యంగా', kn: 'ಹಗುರ, ಆರೋಗ್ಯಕರ', ml: 'ലഘുവും ആരോഗ്യകരവും' },
    sweet: { en: 'Sweet tooth', ta: 'இனிப்பா', tg: 'inippa', hi: 'कुछ मीठा', te: 'తీపి', kn: 'ಸಿಹಿ', ml: 'മധുരം' },
    protein: { en: 'Protein fix', ta: 'புரதம்', tg: 'puradham', hi: 'प्रोटीन वाला', te: 'ప్రోటీన్', kn: 'ಪ್ರೋಟೀನ್', ml: 'പ്രോട്ടീൻ' },
    rainy: { en: 'Rainy day', ta: 'மழை நாள்', tg: 'mazhai naal', hi: 'बारिश का दिन', te: 'వర్షం రోజు', kn: 'ಮಳೆಗಾಲದ ದಿನ', ml: 'മഴദിവസം' },
  },
  region: {
    tamilnadu: { en: 'Tamil Nadu', ta: 'தமிழ்நாடு', tg: 'tamil nadu', hi: 'तमिलनाडु', te: 'తమిళనాడు', kn: 'ತಮಿಳುನಾಡು', ml: 'തമിഴ്നാട്' },
    chettinad: { en: 'Chettinad', ta: 'செட்டிநாடு', tg: 'chettinadu', hi: 'चेट्टिनाड', te: 'చెట్టినాడ్', kn: 'ಚೆಟ್ಟಿನಾಡ್', ml: 'ചെട്ടിനാട്' },
    kongu: { en: 'Kongu', ta: 'கொங்கு', tg: 'kongu', hi: 'कोंगु', te: 'కొంగు', kn: 'ಕೊಂಗು', ml: 'കൊങ്ങ്' },
    madurai: { en: 'Madurai', ta: 'மதுரை', tg: 'madurai', hi: 'मदुरै', te: 'మదురై', kn: 'ಮಧುರೈ', ml: 'മധുര' },
    kerala: { en: 'Kerala', ta: 'கேரளா', tg: 'kerala', hi: 'केरल', te: 'కేరళ', kn: 'ಕೇರಳ', ml: 'കേരളം' },
    andhra: { en: 'Andhra', ta: 'ஆந்திரா', tg: 'aandhra', hi: 'आंध्र', te: 'ఆంధ్ర', kn: 'ಆಂಧ್ರ', ml: 'ആന്ധ്ര' },
    'north-indian': { en: 'North Indian', ta: 'வட இந்தியா', tg: 'vada india', hi: 'उत्तर भारतीय', te: 'ఉత్తర భారత', kn: 'ಉತ್ತರ ಭಾರತ', ml: 'ഉത്തരേന്ത്യൻ' },
    global: { en: 'Global', ta: 'உலகம்', tg: 'ulagam', hi: 'दुनिया भर से', te: 'ప్రపంచ వంటకాలు', kn: 'ಜಾಗತಿಕ', ml: 'ലോക വിഭവങ്ങൾ' },
  },
  meal: {
    tiffin: { en: 'Tiffin', ta: 'டிஃபன்', tg: 'tiffin', hi: 'नाश्ता', te: 'టిఫిన్', kn: 'ತಿಂಡಿ', ml: 'പ്രാതൽ' },
    lunch: { en: 'Lunch', ta: 'மதிய சாப்பாடு', tg: 'madhiya saappadu', hi: 'दोपहर का खाना', te: 'మధ్యాహ్న భోజనం', kn: 'ಮಧ್ಯಾಹ್ನದ ಊಟ', ml: 'ഉച്ചഭക്ഷണം' },
    snack: { en: 'Evening snack', ta: 'மாலை சிற்றுண்டி', tg: 'maalai sitrundi', hi: 'शाम का नाश्ता', te: 'సాయంత్రం స్నాక్', kn: 'ಸಂಜೆ ತಿಂಡಿ', ml: 'വൈകുന്നേര പലഹാരം' },
    dinner: { en: 'Dinner', ta: 'இரவு', tg: 'iravu', hi: 'रात का खाना', te: 'రాత్రి భోజనం', kn: 'ರಾತ್ರಿ ಊಟ', ml: 'അത്താഴം' },
    lunchbox: { en: 'Lunchbox', ta: 'லஞ்ச் பாக்ஸ்', tg: 'lunch box', hi: 'लंचबॉक्स', te: 'లంచ్‌బాక్స్', kn: 'ಲಂಚ್‌ಬಾಕ್ಸ್', ml: 'ലഞ്ച്ബോക്സ്' },
  },
};

/** An album's name in the reader's language: one name, never a second-language line. */
export function albumNames(c: Pick<AlbumCover, 'kind' | 'key' | 'name'>, script: Script): { primary: string } {
  if (c.kind === 'mine') return { primary: c.name || '' };
  const n = CATALOG[c.kind]?.[c.key];
  if (!n) return { primary: c.key };
  const ui = uiLang();
  if (ui === 'ta' || ui === 'en') return { primary: script === 'ta' ? n.ta : script === 'tg' ? n.tg : n.en };
  return { primary: n[ui] };
}

export interface Skin { bg: string; fg: string; border: string; dark: boolean }

// The six Manjal cover skins (handover §4), in order.
export const SKINS: Skin[] = [
  { bg: '#F2B01E', fg: '#2A1C12', border: '0', dark: false },
  { bg: '#2A1C12', fg: '#FBF6EC', border: '0', dark: true },
  { bg: '#FBE8B8', fg: '#2A1C12', border: '0', dark: false },
  { bg: '#8A5A00', fg: '#FBF6EC', border: '0', dark: true },
  { bg: '#FFFDF8', fg: '#2A1C12', border: '1px solid #E8DCC6', dark: false },
  { bg: '#22170F', fg: '#FBF6EC', border: '0', dark: true },
];

// Fixed skins for the built-in albums so neighbours never share a colour; others hash their ref.
const FIXED: Record<string, number> = {
  'craving:spicy': 0, 'craving:comfort': 1, 'craving:quick': 2, 'craving:teatime': 3, 'craving:light': 4,
  'craving:sweet': 5, 'craving:protein': 0, 'craving:rainy': 1,
  'region:tamilnadu': 0, 'region:chettinad': 1, 'region:kongu': 2, 'region:madurai': 3, 'region:kerala': 4,
  'region:andhra': 5, 'region:north-indian': 2, 'region:global': 3,
  'meal:tiffin': 2, 'meal:lunch': 0, 'meal:snack': 3, 'meal:dinner': 1, 'meal:lunchbox': 4,
};

/** A stable skin per album: "Chettinad" is always the same colour. */
export function skinFor(ref: string): Skin {
  if (ref in FIXED) return SKINS[FIXED[ref]];
  let h = 0;
  for (const ch of ref) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return SKINS[h % SKINS.length];
}

export function albumHref(ref: string): string {
  const [kind, key] = ref.split(':');
  return `/albums/${kind}/${encodeURIComponent(key)}`;
}
